import { Button, Pagination, Tooltip } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { createAdminNotification } from "../../utils/notificationService";
import { FiEye, FiTrash2 } from "react-icons/fi";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Table } from "reactstrap";
import { deleteDocument, saveData } from "../../firebase/utility";
import useRowSelection from "../../views/Admin/customHooks/useRowSelection";
import NotFoundDataUI from "../cards/NotFoundDataUI";
import DeleteModal from "../modals/DeleteModal";

const CustomersTable = ({
  customers,
  setSelectedData,
  page,
  setPage,
  pageSize,
  setPageSize,
  totalPages,
  gettingCustomers,
  onClearSearch,
  onCreateNew,
  onMerge,
}) => {
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const [modal, setModal] = useState(false);
  const [id, setId] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const toggle = () => setModal((prev) => !prev);

  const { selectedRows, handleViewSelected } = useRowSelection();

  const handleYesClick = () => {
    if (id && customerData) {
      deleteDocument("Customers", id).then(() => {
        toast.success(
          `${customerData?.billing?.[0]?.first_name} ${customerData?.billing?.[0]?.last_name} Customer (${id}) Deleted Successfully`
        );
        gettingCustomers();
        setId(null);
        setCustomerData(null);
        toggle();
      });
    }
  };

  useEffect(() => {
    let selected = handleViewSelected(customers);
    setSelectedData(selected);
  }, [selectedRows]);

  const handleApproveCustomer = (row) => {
    setApprovingId(row?._id);
    saveData("Customers", row?._id, { approved: true }).then((res) => {
      if (res) {
        toast.success(
          `${row?.billing?.[0]?.first_name || ""} ${row?.billing?.[0]?.last_name || ""} has been approved successfully`
        );
        createAdminNotification({
          title: "Customer Approved",
          message: `Customer ${row?.billing?.[0]?.first_name || ""} ${row?.billing?.[0]?.last_name || ""} was approved`,
          category: "customer",
          entityId: row?._id,
          targetUrl: `/admin/customer-details/${row?._id}`,
        }).catch(console.error);
        if (gettingCustomers) {
          gettingCustomers();
        }
      }
      setApprovingId(null);
    }).catch((err) => {
      console.error(err);
      toast.error(err?.message || "Failed to approve customer. Please try again.");
      setApprovingId(null);
    });
  };

  return (
    <div>
      <DeleteModal
        modal={modal}
        handleDelete={handleYesClick}
        toggle={toggle}
      />

      <Table
        responsive
        hover
        bordered
        className="mb-0"
        style={{
          fontSize: "14px",
          overflow: "hidden",
          border: "1px solid #e2e8f0"
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#00509d", color: "white" }}>
            <th className="text-center" style={{ borderBottom: "none" }}>Customer ID</th>
            <th className="text-center" style={{ borderBottom: "none" }}>QB ID</th>
            <th className="text-center" style={{ borderBottom: "none" }}>Customer Name</th>
            <th className="text-center" style={{ borderBottom: "none" }}>Company Name</th>
            <th className="text-center" style={{ borderBottom: "none" }}>Customer Email</th>
            <th className="text-center" style={{ borderBottom: "none" }}>Created At</th>
            <th className="text-center" style={{ borderBottom: "none" }}>Status</th>
            <th className="text-center" style={{ borderBottom: "none" }}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {customers.length > 0 ? (
            customers?.map((row, index) => (
              <tr
                key={index}
                className="align-middle"
                style={{
                  backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                  borderBottom: "1px solid #f1f5f9",
                  transition: "background-color 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#ffffff" : "#f8fafc"}
              >
                <td className="text-center text-muted small fw-bold">{row?.id}</td>
                <td className="text-center">
                  {row?.quickbooks_id ? (
                    <span className="fw-semibold text-primary">{row?.quickbooks_id}</span>
                  ) : "-"}
                </td>
                <td className="text-center fw-bold">
                  {row?.billing?.[0]?.first_name} {row?.billing?.[0]?.last_name}
                </td>
                <td className="text-center" style={{ width: "280px" }}>{row?.billing?.[0]?.company || <span className="text-muted italic">B2C Customer</span>}</td>
                <td className="text-center">{row?.billing?.[0]?.email}</td>
                <td className="text-center">
                  {row?.createdAt ? moment(row?.createdAt).format("MM/DD/YYYY") : "-"}
                </td>
                <td className="text-center">
                  <span
                    className={`user-badge px-3 py-1 rounded-pill fw-bold ${row?.approved === true
                      ? "bg-success text-white shadow-sm"
                      : row?.approved === false
                        ? "bg-danger text-white shadow-sm"
                        : "bg-warning text-dark shadow-sm"
                      }`}
                  >
                    {!row?.approved ? (
                      <button
                        onClick={() => handleApproveCustomer(row)}
                        className="border-0 bg-transparent fw-bold p-0 d-flex align-items-center gap-2 m-auto"
                        style={{ color: "inherit", cursor: "pointer" }}
                        disabled={approvingId === row?._id}
                      >
                        {approvingId === row?._id ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            Approving...
                          </>
                        ) : (
                          "Pending Request"
                        )}
                      </button>
                    ) : (
                      "Approved"
                    )}
                  </span>
                </td>
                <td className="text-center">
                  <div className="d-flex gap-2 align-items-center justify-content-center">
                    <Tooltip title="View Customer">
                      <Button
                        color="link"
                        className="p-1 border-0"
                        style={{ transition: 'transform 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.15)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        onClick={() => {
                          history.push({
                            pathname: `/admin/customer-details/${row._id}`,
                            state: row,
                          });
                        }}
                      >
                        <FiEye size={16} className="text-primary" />
                      </Button>
                    </Tooltip>

                    {(user?.role === "Admin" || user?.role === "Super-admin") && (
                      <>


                        <Tooltip title="Delete Customer">
                          <Button
                            color="link"
                            className="p-1 border-0"
                            style={{ transition: 'transform 0.2s' }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.15)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            onClick={() => {
                              setCustomerData(row);
                              setId(row?._id);
                              toggle();
                            }}
                          >
                            <FiTrash2 size={16} className="text-danger" />
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="8" className="text-center py-5">
                <NotFoundDataUI
                  message="Customers not found!"
                  onClearSearch={onClearSearch}
                  onCreateNew={onCreateNew}
                />
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {customers?.length > 0 && (
        <div className="d-flex justify-content-end mt-3 px-2">
          <Pagination
            current={page}
            total={totalPages}
            onChange={(value) => {
              localStorage.setItem("customerPage", value);
              setPage(value);
            }}
            pageSize={pageSize}
            showSizeChanger={true}
            onShowSizeChange={(current, size) => {
              if (setPageSize) setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={['10', '25', '50', '100']}
            size="small"
          />
        </div>
      )}
    </div>
  );
};

export default CustomersTable;
